import { BaseService } from './BaseService';
import type { PagedResult } from '../types/tenant';
import type {
  CreateTranscriptionOptions,
  TranscriptionListFilters,
  TranscriptionTransactionDto,
} from '../types/transcription';

/**
 * Service for the Voice & Transcription module. Uses the same public speech-to-text endpoint that
 * third-party integrators call, so the playground exercises the real API surface.
 */
export class TranscriptionService extends BaseService {
  /**
   * Checks whether the server has speech provider credentials configured, so the UI can warn
   * before a transcription attempt fails at runtime.
   */
  async getConfigStatus(): Promise<{ speechConfigured: boolean }> {
    return this.get<{ speechConfigured: boolean }>('/transcriptions/config-status');
  }

  /**
   * Uploads audio for transcription. Returns the accepted transaction (status Pending); poll
   * {@link getTransaction} or subscribe over the hub for the transcript.
   */
  async createTranscription(
    file: Blob,
    fileName: string,
    options: CreateTranscriptionOptions = {}
  ): Promise<TranscriptionTransactionDto> {
    const form = new FormData();
    form.append('file', file, fileName);

    if (options.mode) form.append('mode', options.mode);
    if (options.language) form.append('language', options.language);
    if (options.timestamps) form.append('timestamps', 'true');
    if (options.diarization) form.append('diarization', 'true');
    if (options.vocabulary?.length) form.append('vocabulary', options.vocabulary.join(','));

    const headers: Record<string, string> = {};
    if (options.clientCorrelationId) {
      headers['X-Correlation-Id'] = options.clientCorrelationId;
    }

    // Agent-scoped jobs go to the agent-slug API (agent Speech config + prompt correction);
    // unscoped jobs use the agent-agnostic audio API. Both return snake_case; normalize to the
    // camelCase shape the UI uses.
    const path = options.agentSlug
      ? `/v1/agents/${encodeURIComponent(options.agentSlug)}/transcriptions`
      : '/v1/audio/transcriptions';
    const raw = await this.post<Record<string, unknown>>(path, form, { headers });
    return normalizeTransaction(raw);
  }

  /** Gets a transaction from the public API (transcript only, no event timeline). */
  async getPublicTransaction(id: string, agentSlug?: string): Promise<TranscriptionTransactionDto> {
    const path = agentSlug
      ? `/v1/agents/${encodeURIComponent(agentSlug)}/transcriptions/${id}`
      : `/v1/audio/transcriptions/${id}`;
    const raw = await this.get<Record<string, unknown>>(path);
    return normalizeTransaction(raw);
  }

  /** Lists transactions for the operator troubleshooting view. */
  async listTransactions(filters: TranscriptionListFilters = {}): Promise<PagedResult<TranscriptionTransactionDto>> {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.mode) params.set('mode', filters.mode);
    if (filters.correlationId) params.set('correlationId', filters.correlationId);
    if (filters.clientCorrelationId) params.set('clientCorrelationId', filters.clientCorrelationId);
    if (filters.searchTerm) params.set('searchTerm', filters.searchTerm);
    if (filters.fromUtc) params.set('fromUtc', filters.fromUtc);
    if (filters.toUtc) params.set('toUtc', filters.toUtc);
    params.set('pageNumber', String(filters.pageNumber ?? 1));
    params.set('pageSize', String(filters.pageSize ?? 25));

    return this.get<PagedResult<TranscriptionTransactionDto>>(`/transcriptions?${params.toString()}`);
  }

  /** Gets one transaction with its full troubleshooting timeline. */
  async getTransaction(id: string): Promise<TranscriptionTransactionDto> {
    return this.get<TranscriptionTransactionDto>(`/transcriptions/${id}`);
  }

  /** Gets every transaction recorded under one correlation. */
  async getByCorrelation(correlationId: string): Promise<TranscriptionTransactionDto[]> {
    return this.get<TranscriptionTransactionDto[]>(`/transcriptions/by-correlation/${correlationId}`);
  }
}

const normalizeTransaction = (raw: Record<string, unknown>): TranscriptionTransactionDto => {
  const pick = <T,>(camel: string, snake: string): T => (raw[camel] ?? raw[snake]) as T;

  return {
    id: pick<string>('id', 'id'),
    correlationId: pick<string>('correlationId', 'correlation_id'),
    clientCorrelationId: pick<string | null>('clientCorrelationId', 'client_correlation_id'),
    mode: pick('mode', 'mode'),
    status: pick('status', 'status'),
    language: pick<string>('language', 'language'),
    optionsJson: pick<string | null>('optionsJson', 'options_json'),
    fileName: pick<string | null>('fileName', 'file_name'),
    audioSizeBytes: pick<number | null>('audioSizeBytes', 'audio_size_bytes'),
    text: pick<string | null>('text', 'text'),
    agentSlug: pick<string | null>('agentSlug', 'agent_slug'),
    correctedText: pick<string | null>('correctedText', 'corrected_text'),
    correctionSessionId: pick<string | null>('correctionSessionId', 'correction_session_id'),
    durationSeconds: pick<number | null>('durationSeconds', 'duration_seconds'),
    provider: pick<string>('provider', 'provider'),
    errorCode: pick<string | null>('errorCode', 'error_code'),
    errorMessage: pick<string | null>('errorMessage', 'error_message'),
    httpRequestId: pick<string | null>('httpRequestId', 'http_request_id'),
    startedAt: pick<string | null>('startedAt', 'started_at'),
    completedAt: pick<string | null>('completedAt', 'completed_at'),
    createdAt: pick<string>('createdAt', 'created_at'),
    events: [],
  };
};

export const transcriptionService = new TranscriptionService();
